import { useEffect, useState } from "react";
import { Link } from "react-router";
import { appPaths } from "../../shared/routing/appPaths";
import { useItemNames } from "./itemNameContext";
import type { ItemNameCache } from "./itemNameCache";
import { shortReference } from "../../shared/references/shortReferences";

/**
 * A pinned item version as people read it: the item's name and its short version
 * (authoring-ui.md AU-8: "pinned references shown by item name and short version"),
 * linking to the item page. Names are read once per reference and kept for the session;
 * one that cannot be read shows the reference itself, or `fallback` where one is given.
 * Without `showVersion` only the name shows; the full reference stays in the tooltip.
 */

interface ItemReferenceNameProps {
  reference: string;
  link?: boolean;
  showVersion?: boolean;
  fallback?: string;
  /**
   * The item's name when the caller reads the item itself: `null` while it does, or if it
   * could not. Left out, the name is looked up here.
   */
  knownName?: string | null;
}

export default function ItemReferenceName({
  reference,
  link = true,
  showVersion = true,
  fallback,
  knownName,
}: ItemReferenceNameProps) {
  const cache = useItemNames();
  const [resolved, setResolved] = useState<{ cache: ItemNameCache; reference: string; name: string | null } | null>(null);
  const name = knownName !== undefined ? knownName : resolved?.cache === cache && resolved.reference === reference ? resolved.name : null;
  useEffect(() => {
    if (knownName !== undefined) {
      return;
    }
    let current = true;
    void cache.nameOf(reference).then((found) => {
      if (current) {
        setResolved({ cache, reference, name: found });
      }
    });
    return () => {
      current = false;
    };
  }, [cache, knownName, reference]);
  const text = (
    <>
      {name ?? fallback ?? <span className="font-mono">{shortReference("itemId", reference)}…</span>}
      {showVersion ? <span className="app-text-muted font-mono"> @{shortReference("version", reference)}</span> : null}
    </>
  );
  return link ? (
    <Link to={appPaths.item(reference)} className="app-link underline" title={reference}>
      {text}
    </Link>
  ) : (
    <span title={reference}>{text}</span>
  );
}
